import { ApolloClient, InMemoryCache, createHttpLink } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import {
  GET_PRESIGNED_URLS_QUERY,
  GET_LIBRARY_CARD_QUERY,
  GET_STARTUP_BY_USER_QUERY,
  LIST_LIBRARY_CARDS_QUERY,
  SAVE_LIBRARY_CARD_MUTATION,
  PUT_FILE_EVENT_MUTATION,
  INITIATE_MULTIPART_UPLOAD_MUTATION,
  GET_PRESIGNED_URL_FOR_PART_QUERY,
  COMPLETE_MULTIPART_UPLOAD_MUTATION,
} from "../queries/libraryCardQueries";

import { GET_LIBRARY_CARD_INDEX_TAGS_QUERY } from "../queries/getLibraryCardIndexTagsQuery";
import { getAuthToken, clearAuthState } from "../utils/authUtils";

const GRAPHQL_ENDPOINT =
  "https://m2jxbyhagffjthxtsiuwb2iiwe.appsync-api.us-east-1.amazonaws.com/graphql";

// Temporary fallback API key for development (remove once backend is configured for Cognito)
// Set to null to disable fallback and use only JWT tokens
const FALLBACK_API_KEY = null;

// Create HTTP link
const httpLink = createHttpLink({
  uri: GRAPHQL_ENDPOINT,
});

// Create auth link that adds JWT token to headers
const authLink = setContext((_, { headers }) => {
  // Get the JWT token from localStorage (getAuthToken validates expiration)
  const token = getAuthToken();

  // Enhanced debug logging
  if (token) {
    console.log("libraryCardApi - JWT token found:", {
      tokenLength: token.length,
      tokenPreview: token.substring(0, 20) + "...",
      hasToken: !!token,
    });
    console.log("libraryCardApi - Adding Authorization header to request");
  } else {
    console.error("libraryCardApi - ❌ No valid JWT token found!");
    console.error(
      "libraryCardApi - Token may be expired or missing - user needs to log in",
    );
  }

  // Return the headers to the context so httpLink can read them
  // For AWS AppSync with Cognito, use token without "Bearer" prefix
  // Fallback to API key if token not available and fallback is enabled
  let authHeaders = {};

  if (token) {
    // Primary: Use Cognito ID token
    authHeaders = { Authorization: token };
  } else if (FALLBACK_API_KEY) {
    // Fallback: Use API key (for development only)
    console.warn(
      "libraryCardApi - Using fallback API key (JWT token not available)",
    );
    authHeaders = { "x-api-key": FALLBACK_API_KEY };
  }

  console.log("libraryCardApi - Request headers:", {
    hasAuthorization: !!authHeaders.Authorization,
    hasApiKey: !!authHeaders["x-api-key"],
    headerKeys: Object.keys({ ...headers, ...authHeaders }),
  });

  return {
    headers: {
      ...headers,
      ...authHeaders,
    },
  };
});

// Create error link to handle 401 Unauthorized (expired tokens)
const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, extensions }) => {
      // Handle UnauthorizedException (expired/invalid token)
      if (
        extensions?.errorType === "UnauthorizedException" ||
        message.includes("Unauthorized")
      ) {
        console.error(
          "libraryCardApi - Unauthorized error detected, token may be expired",
        );
        // Clear auth state and redirect to login
        clearAuthState();
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
      }
    });
  }

  if (networkError) {
    // Handle 401 network errors
    if (networkError.statusCode === 401) {
      console.error(
        "libraryCardApi - 401 Unauthorized network error, token may be expired",
      );
      clearAuthState();
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }
  }
});

const client = new ApolloClient({
  link: errorLink.concat(authLink).concat(httpLink),
  cache: new InMemoryCache(),
});

export const fetchLibraryCardFromApi = async (input) => {
  try {
    console.log(
      `PMM - SF - libraryCardApi.js - fetchLibraryCardFromApi - input is: ${JSON.stringify(
        input,
      )}`,
    );
    const apiPayload = {
      query: GET_LIBRARY_CARD_QUERY,
      variables: { input },
      fetchPolicy: "network-only",
    };
    console.log(
      `PMM - SF - libraryCardApi.js - fetchLibraryCardFromApi - apiPayload is: ${JSON.stringify(
        apiPayload,
      )}`,
    );
    const response = await client.query(apiPayload);

    console.log(
      `PMM - SF - libraryCardApi.js - Full response: ${JSON.stringify(
        response,
      )}`,
    );
    console.log(
      `PMM - SF - libraryCardApi.js - response.data: ${JSON.stringify(
        response?.data,
      )}`,
    );
    console.log(
      `PMM - SF - libraryCardApi.js - response.errors: ${JSON.stringify(
        response?.errors,
      )}`,
    );
    console.log(
      `PMM - SF - libraryCardApi.js - response.data.getLibraryCard: ${JSON.stringify(
        response?.data?.getLibraryCard,
      )}`,
    );

    // Check for GraphQL errors
    if (response.errors && response.errors.length > 0) {
      console.error(
        `PMM - SF - libraryCardApi.js - GraphQL errors:`,
        response.errors,
      );
      // If getLibraryCard is null and there are errors, return null
      // The component will handle this gracefully
      if (response?.data?.getLibraryCard === null) {
        console.log(
          `PMM - SF - libraryCardApi.js - getLibraryCard is null with errors, returning null`,
        );
        return null;
      }
    }

    console.log(
      `PMM - SF - libraryCardApi.js - response?.data?.getLibraryCard?.data is: ${JSON.stringify(
        response?.data?.getLibraryCard?.data,
      )}`,
    );
    const result = response?.data?.getLibraryCard?.data;
    console.log(
      `PMM - SF - libraryCardApi.js - Returning result: ${JSON.stringify(
        result,
      )}`,
    );
    return result;
  } catch (error) {
    console.log(
      `PMM - SF - libraryCardApiljs - error is: ${JSON.stringify(error)}`,
    );
  }
};

export const listLibraryCardsFromApi = async (input) => {
  console.log(
    `PMM - SF - libraryCardApi.js - listLibraryCardsFromApi - input is: ${JSON.stringify(
      input,
    )}`,
  );
  const apiPayload = {
    query: LIST_LIBRARY_CARDS_QUERY,
    variables: { input },
    // Force network-only to bypass Apollo cache and get fresh data
    fetchPolicy: "network-only",
  };
  console.log(
    `PMM - SF - libraryCardApi.js - listLibraryCardsFromApi - apiPayload is: ${JSON.stringify(
      apiPayload,
    )}`,
  );
  const response = await client.query(apiPayload);
  console.log(
    `PMM - SF - libraryCardApi.js - listLibraryCardsFromApi - response.data.listLibraryCards:`,
    response.data.listLibraryCards,
  );
  return response.data.listLibraryCards;
};

export const saveLibraryCardToApi = async (input) => {
  console.log(
    `PMM - SF - libraryCardApi.js - saveLibraryCardToApi - input is: ${JSON.stringify(
      input,
    )}`,
  );
  const apiPayload = {
    mutation: SAVE_LIBRARY_CARD_MUTATION,
    variables: { input },
  };

  console.log(
    `PMM - SF - libraryCardApi.js - saveLibraryCardToApi - apiPayload is: ${JSON.stringify(
      apiPayload,
    )}`,
  );
  const response = await client.mutate(apiPayload);
  // console.log(
  //   `PMM - SF - libraryCardApi.js - saveLibraryCardToApi - response is: ${JSON.stringify(
  //     response
  //   )}`
  // );
  return response;
};

export const createLibraryCardInApi = saveLibraryCardToApi;
export const updateLibraryCardInApi = saveLibraryCardToApi;

export const deleteLibraryCardFromApi = async (input) => {
  console.log("deleteLibraryCardFromApi not implemented yet");
  throw new Error("deleteLibraryCardFromApi not implemented yet");
};

export const fetchPresignedUrlsFromApi = async (actionPayload) => {
  // Ensure operationType is set (but don't mutate the original object)
  const payload = {
    ...actionPayload,
    operationType: actionPayload.operationType || "GET_PRESIGNED_URLS",
  };

  // Validate required fields
  if (!payload.entityType || typeof payload.entityType !== "string") {
    console.error(
      "libraryCardApi - Invalid entityType:",
      payload.entityType,
      typeof payload.entityType,
    );
    throw new Error("entityType is required and must be a string");
  }

  if (!payload.tenantId || typeof payload.tenantId !== "string") {
    console.error(
      "libraryCardApi - Invalid tenantId:",
      payload.tenantId,
      typeof payload.tenantId,
    );
    throw new Error("tenantId is required and must be a string");
  }

  if (!Array.isArray(payload.fileNames) || payload.fileNames.length === 0) {
    console.error(
      "libraryCardApi - Invalid fileNames:",
      payload.fileNames,
      typeof payload.fileNames,
    );
    throw new Error("fileNames is required and must be a non-empty array");
  }

  // Validate all fileNames are strings
  const invalidFileNames = payload.fileNames.filter(
    (name) => !name || typeof name !== "string" || name.trim().length === 0,
  );
  if (invalidFileNames.length > 0) {
    console.error(
      "libraryCardApi - Invalid file names found:",
      invalidFileNames,
    );
    throw new Error("All fileNames must be non-empty strings");
  }

  const apiPayload = {
    query: GET_PRESIGNED_URLS_QUERY,
    variables: {
      input: payload,
    },
    fetchPolicy: "no-cache", // always fetch fresh — presigned URLs must never be served from Apollo cache
  };

  console.log("libraryCardApi - Sending GraphQL request:", {
    operationType: payload.operationType,
    entityType: payload.entityType,
    tenantId: payload.tenantId,
    bucket: payload.bucket,
    fileNamesCount: payload.fileNames.length,
    firstFewFileNames: payload.fileNames.slice(0, 3),
  });

  try {
    const response = await client.query(apiPayload);
    console.log("libraryCardApi - GraphQL response received:", {
      hasData: !!response.data,
      hasErrors: !!response.errors,
      presignedUrlsCount:
        response.data?.getPresignedUrls?.presignedUrls?.length || 0,
    });
    return response.data.getPresignedUrls.presignedUrls;
  } catch (error) {
    console.error("libraryCardApi - Error fetching presigned URLs:", {
      message: error.message,
      graphQLErrors: error.graphQLErrors,
      networkError: error.networkError,
      fullError: error,
    });
    throw error;
  }
};

export const putFileEventToApi = async (input) => {
  const apiPayload = {
    mutation: PUT_FILE_EVENT_MUTATION,
    variables: { input },
  };
  const response = await client.mutate(apiPayload);
  return response.data.putFileEvent;
};

export const uploadFileToApi = putFileEventToApi;

export const fetchMetadataFromApi = async (input) => {
  console.log("fetchMetadataFromApi not implemented yet");
  throw new Error("fetchMetadataFromApi not implemented yet");
};

export const fetchLibraryCardDetailsFromApi = fetchLibraryCardFromApi;

export const fetchLibraryCardsFromApi = async (input) => {
  console.log(
    `PMM - SF - api - fetchLibraryCardsFromApi - input: ${JSON.stringify(
      input,
    )}`,
  );

  try {
    const apiPayload = {
      query: LIST_LIBRARY_CARDS_QUERY, // This uses the list query
      variables: { input },
      // Force network-only to bypass Apollo cache and get fresh data
      fetchPolicy: "network-only",
    };
    const response = await client.query(apiPayload);
    console.log(
      `PMM - SF - libraryCardApi.js - fetchLibraryCardsFromApi - response.data.listLibraryCards:`,
      response.data.listLibraryCards,
    );
    return response.data.listLibraryCards;
  } catch (error) {
    console.error(
      "PMM - SF - libraryCardApi.js - fetchLibraryCardsFromApi - error:",
      error,
    );
    throw error;
  }
};

// Fetch STARTUPRECORD by Cognito userId — uses CreatedByIndex GSI on the backend
export const fetchStartupByUserFromApi = async (userId) => {
  try {
    const response = await client.query({
      query: GET_STARTUP_BY_USER_QUERY,
      variables: { input: { operationType: "GET_STARTUP_BY_USER", userId } },
      fetchPolicy: "network-only",
    });
    return response?.data?.getStartupByUser ?? null;
  } catch (error) {
    console.error(`fetchStartupByUserFromApi error: ${error.message}`);
    return null;
  }
};

export const getLibraryCardTagsApi = async (payload) => {
  // console.log(
  //   `PMM - SF - libraryCardApi.js - getTagsApi payload is: ${JSON.stringify(
  //     payload
  //   )}`
  // );

  try {
    const response = await client.query({
      query: GET_LIBRARY_CARD_INDEX_TAGS_QUERY, // Use the GraphQL query to fetch media index tags
      variables: { input: payload },
    });

    // console.log(
    //   `PMM - SF - libraryCardApi.js - getLibraryCardTagsApi response is: ${JSON.stringify(
    //     response
    //   )}`
    // );
    return response;
  } catch (error) {
    console.error(
      `PMM - SF - libraryCardApi.js - Error fetching tags: ${error.message}`,
    );
    throw error;
  }
};
